// fleet — B-1b „Wersje i flota" (MONIT 2026-09-12_1750, F2/F4/F5): strona telefonu.
//
// F2 deviceId: UUID w IDB (store `settings`, klucz `device_id`), generowany raz po pierwszym
//   uruchomieniu po B-1b, NIGDY nie zmieniany; przeżywa wylogowanie i zmianę teamu (clearAll
//   nie rusza `settings`); ginie tylko z danymi przeglądarki (= nowe urządzenie w tabeli).
//   Etykieta (auto „<Team> #n" z serwera + własna nazwa) trzymana lokalnie do stopki/Serwisu.
// F4 puls: POST /api/fleet/heartbeat ~1 KB (limit 6 s). Kiedy: start, po instalacji SW,
//   przełączenie Trybu lokalnego (jeden puls w obie strony), ręcznie; powrót do aplikacji
//   (visible) i inne — nie częściej niż co 10 min (HEARTBEAT_MIN_GAP_MS). W Trybie lokalnym
//   poza pulsem przełączenia: BRAK (DN1 = a, doktryna „zero sieci"). Odpowiedź = etykieta +
//   latest/min_required (zapis lokalny; źródło dla kontroli wersji F3).
// F5 diagnostyka automatyczna: POST /api/fleet/diagnostics (pełny pakiet z diagnosticsPack).
//   Wyzwalacze: version_mismatch (raz per para skorupa→latest), sw_install_failed (3 padnięte
//   instalacje), upload_failures (3 kolejne timeout/network/5xx), shell_error (onerror /
//   unhandledrejection / ErrorBoundary). Limit 1 / 30 min per powód; ręczna zawsze. W Trybie
//   lokalnym kolejkowana lokalnie (max 5) i wysyłana przy wyjściu z trybu.
//
// Czyste decyzje (shouldSendHeartbeat, shouldSendDiagnostics, parseDeviceModel, isUploadStreakError)
// testowalne w node:test; stan trwały w IDB (mock backing w testach).

import { getSetting, setSetting, getQueueStats, getUploadLog } from './offlineDB.js';
import { apiFetch } from '../hooks/useApi.js';
import { getToken } from '../hooks/useAuth.js';
import { isLocalMode, subscribeLocalMode } from './localMode.js';
import { getLastSuccessAt } from './queueHealth.js';
import { getNetState } from './netActivity.js';
import { APP_VERSION, compareVersions } from '../shared/appVersion.js';
import { buildDiagnosticsPack } from './diagnosticsPack.js';
import { setDeviceIdentity } from './deviceIdentity.js'; // v85: urządzenie w audycie usunięcia zdjęcia

export const KEY_DEVICE_ID = 'device_id';
export const KEY_DEVICE_LABEL = 'device_label';           // { auto, custom, display, at }
export const KEY_SERVER_INFO = 'fleet_server_info';       // { latest, minRequired, at }
export const KEY_HEARTBEAT_AT = 'fleet_heartbeat_at';
export const KEY_DIAG_LAST = 'fleet_diag_last';           // { [reason]: ts }
export const KEY_DIAG_QUEUE = 'fleet_diag_queue';         // [ { reason, pack, at } ] max 5
export const KEY_MISMATCH_SEEN = 'fleet_mismatch_seen';   // 'v72>v73'
export const KEY_SW_INSTALL_FAIL = 'fleet_sw_install_fail'; // { version, count, reported }

export const HEARTBEAT_MIN_GAP_MS = 10 * 60 * 1000;
export const HEARTBEAT_TIMEOUT_MS = 6_000;
export const DIAG_MIN_GAP_MS = 30 * 60 * 1000;
export const DIAG_QUEUE_MAX = 5;
export const UPLOAD_FAIL_STREAK = 3;
export const SW_INSTALL_FAIL_MAX = 3;
/** Powody pulsu, które ZAWSZE go wysyłają (reszta: nie częściej niż co 10 min). */
export const HEARTBEAT_ALWAYS_REASONS = Object.freeze(['start', 'sw-installed', 'local-mode-on', 'local-mode-off', 'manual']);
export const DIAG_REASONS = Object.freeze(['manual', 'version_mismatch', 'sw_install_failed', 'upload_failures', 'shell_error']);

// ─── Stan w pamięci ──────────────────────────────────────────────────────────
let deviceId = null;
let label = null;         // { auto, custom, display, at }
let serverInfo = null;    // { latest, minRequired, at }
let lastHeartbeatAt = 0;
let diagLast = {};
let mismatchSeen = null;
let swInstallFail = { version: null, count: 0, reported: false };
let uploadFailStreak = 0;
let loaded = false;
const listeners = new Set();

function emit() {
  const snap = getFleetState();
  for (const fn of listeners) {
    try { fn(snap); } catch { /* słuchacz nie wywraca nadawcy */ }
  }
}

export function subscribeFleet(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getFleetState() {
  return { deviceId, label, serverInfo, lastHeartbeatAt, swInstallFailed: swInstallFail.count >= SW_INSTALL_FAIL_MAX, loaded };
}

function newUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Fallback (bardzo stare WebView): losowy UUID v4 z Math.random.
  const h = '0123456789abcdef';
  let s = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) s += '-';
    else if (i === 14) s += '4';
    else if (i === 19) s += h[(Math.random() * 4 | 0) + 8];
    else s += h[Math.random() * 16 | 0];
  }
  return s;
}

/** Ładuje stan z IDB (raz, przy starcie); tworzy deviceId, gdy go nie ma. Błąd IDB = deviceId ulotny. */
export async function initFleet() {
  try {
    const id = await getSetting(KEY_DEVICE_ID);
    if (typeof id === 'string' && id.length === 36) deviceId = id;
    else {
      deviceId = newUuid();
      await setSetting(KEY_DEVICE_ID, deviceId).catch(() => {});
    }
    const [l, s, hb, dl, ms, sf] = await Promise.all([
      getSetting(KEY_DEVICE_LABEL), getSetting(KEY_SERVER_INFO), getSetting(KEY_HEARTBEAT_AT),
      getSetting(KEY_DIAG_LAST), getSetting(KEY_MISMATCH_SEEN), getSetting(KEY_SW_INSTALL_FAIL),
    ]);
    label = l && typeof l === 'object' ? l : null;
    serverInfo = s && typeof s === 'object' ? s : null;
    lastHeartbeatAt = Number(hb) || 0;
    diagLast = dl && typeof dl === 'object' ? dl : {};
    mismatchSeen = typeof ms === 'string' ? ms : null;
    swInstallFail = sf && typeof sf === 'object' ? { version: sf.version ?? null, count: Number(sf.count) || 0, reported: !!sf.reported } : { version: null, count: 0, reported: false };
  } catch {
    if (!deviceId) deviceId = newUuid();
  }
  setDeviceIdentity(deviceId);
  loaded = true;
  emit();
  return deviceId;
}

export function getDeviceId() { return deviceId; }
export function getDeviceLabel() { return label; }
export function getServerInfo() { return serverInfo; }
export function isFleetLoaded() { return loaded; }

// ─── Czyste decyzje ──────────────────────────────────────────────────────────

/**
 * Model z user agenta. Android: token przed „ Build/" (np. „SM-A576B"); iPhone/iPad: samo
 * „iPhone"/„iPad" (iOS nie zdradza modelu); inne → null.
 */
export function parseDeviceModel(ua) {
  const s = String(ua || '');
  if (/iPhone/.test(s)) return 'iPhone';
  if (/iPad/.test(s) || (/Macintosh/.test(s) && /Mobile/.test(s))) return 'iPad';
  const build = /;\s*([^;()]+?)\s+Build\//.exec(s);
  if (build) return build[1].trim().slice(0, 60);
  const android = /Android[^;)]*;\s*([^;)]+)\)/.exec(s);
  if (android) {
    const m = android[1].trim();
    if (m && !/^[a-z]{2}(-[a-z]{2})?$/i.test(m)) return m.slice(0, 60);
  }
  return null;
}

/** Rozmiar ekranu „412x915@2.6" (CSS px + DPR). */
export function screenInfo() {
  try {
    const w = typeof screen !== 'undefined' ? screen.width : null;
    const h = typeof screen !== 'undefined' ? screen.height : null;
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? Math.round(window.devicePixelRatio * 10) / 10 : null;
    if (!w || !h) return null;
    return `${w}x${h}${dpr ? `@${dpr}` : ''}`;
  } catch { return null; }
}

/** Czy wysłać puls: powody z listy ZAWSZE; inne nie częściej niż co 10 min. */
export function shouldSendHeartbeat(reason, lastAt, now = Date.now()) {
  if (HEARTBEAT_ALWAYS_REASONS.includes(reason)) return true;
  return !lastAt || now - lastAt >= HEARTBEAT_MIN_GAP_MS;
}

/** Czy wysłać diagnostykę: ręczna zawsze; inne 1 / 30 min per powód. */
export function shouldSendDiagnostics(reason, lastMap, now = Date.now()) {
  if (reason === 'manual') return true;
  const last = Number(lastMap?.[reason]) || 0;
  return !last || now - last >= DIAG_MIN_GAP_MS;
}

/** Błąd wysyłki liczony do serii (timeout / sieć / 5xx); 4xx trwałe (400/409) nie. */
export function isUploadStreakError(error) {
  const e = String(error || '');
  return /timeout|network|HTTP 5\d\d/i.test(e);
}

/** Stan sieci do pulsu: offline | weak | online. */
export function netStateLabel() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return getNetState().weak ? 'weak' : 'online';
}

// ─── Puls ────────────────────────────────────────────────────────────────────

/**
 * Buduje payload pulsu (~1 KB). `sw` = fetchSwRegistrationInfo() ({active, installing, waiting}).
 */
export async function buildHeartbeat({ reason, sw } = {}) {
  const [stats, log] = await Promise.all([
    getQueueStats().catch(() => ({ total: 0, bytes: 0 })),
    getUploadLog(1).catch(() => []),
  ]);
  const active = sw?.active || null;
  const last = log[0] || null;
  const lastSuccess = getLastSuccessAt();
  return {
    deviceId,
    appVersion: APP_VERSION,
    swVersion: active?.version || null,
    swPrecached: active?.precached ?? null,
    swExpected: active?.expected ?? null,
    swWaiting: !!sw?.waiting,
    swInstalling: !!sw?.installing,
    swInstallFailed: swInstallFail.count >= SW_INSTALL_FAIL_MAX,
    model: typeof navigator !== 'undefined' ? parseDeviceModel(navigator.userAgent) : null,
    screen: screenInfo(),
    userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent).slice(0, 400) : null,
    queueCount: Number(stats.total) || 0,
    queueBytes: Number(stats.bytes) || 0,
    localMode: !!isLocalMode(),
    netState: netStateLabel(),
    lastUpload: last ? { result: last.result || null, at: last.at || null } : null,
    lastUploadSuccessAt: lastSuccess || null,
    clientTime: new Date().toISOString(),
    reason: reason || 'unknown',
  };
}

async function storeServerInfo(res) {
  serverInfo = { latest: res?.latest || null, minRequired: res?.minRequired || null, at: Date.now() };
  await setSetting(KEY_SERVER_INFO, serverInfo).catch(() => {});
}

async function storeLabel(res) {
  const auto = res?.labelAuto || null;
  const custom = res?.labelCustom || null;
  label = { auto, custom, display: custom || auto || null, at: Date.now() };
  await setSetting(KEY_DEVICE_LABEL, label).catch(() => {});
}

/**
 * Wysyła puls (gdy wolno). Zwraca odpowiedź serwera ({ latest, minRequired, label… }) albo null
 * (pominięty / offline / Tryb lokalny / błąd — nigdy wyjątek).
 * @param {{reason:string, sw?:object, force?:boolean}} p
 */
export async function sendHeartbeat({ reason = 'unknown', sw = null, force = false } = {}) {
  if (!deviceId || !getToken()) return null;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
  const localToggle = reason === 'local-mode-on' || reason === 'local-mode-off';
  if (isLocalMode() && !localToggle && !force) return null; // DN1 = (a): zero sieci w Trybie lokalnym
  if (!force && !shouldSendHeartbeat(reason, lastHeartbeatAt)) return null;
  try {
    const body = await buildHeartbeat({ reason, sw });
    const res = await apiFetch('POST', '/api/fleet/heartbeat', body, { timeoutMs: HEARTBEAT_TIMEOUT_MS });
    lastHeartbeatAt = Date.now();
    await setSetting(KEY_HEARTBEAT_AT, lastHeartbeatAt).catch(() => {});
    await storeServerInfo(res);
    await storeLabel(res);
    emit();
    // Po powrocie z Trybu lokalnego: zaległe diagnostyki.
    if (reason === 'local-mode-off') flushDiagnosticsQueue().catch(() => {});
    return res;
  } catch {
    return null;
  }
}

/** Własna nazwa urządzenia (PUT; null = skasuj). Zwraca { ok, label } albo { ok:false, error }. */
export async function setDeviceCustomLabel(custom) {
  if (!deviceId) return { ok: false, error: 'brak deviceId' };
  try {
    const res = await apiFetch('PUT', `/api/fleet/devices/${deviceId}/label`, { label: custom }, { timeoutMs: HEARTBEAT_TIMEOUT_MS });
    await storeLabel(res);
    emit();
    return { ok: true, label };
  } catch (err) {
    return { ok: false, error: err?.message || String(err) };
  }
}

// ─── Diagnostyka ─────────────────────────────────────────────────────────────

async function postDiagnostics(reason, pack) {
  const body = { ...pack, deviceId, reason, label: label?.display || null };
  await apiFetch('POST', '/api/fleet/diagnostics', body, { timeoutMs: 20_000 });
}

/**
 * Wysyła (albo kolejkuje w Trybie lokalnym) pakiet diagnostyczny.
 * @returns {Promise<{status:'sent'|'queued'|'skipped'|'failed', error?:string}>}
 */
export async function sendDiagnostics(reason = 'manual', { extra = {}, pack = null } = {}) {
  const r = DIAG_REASONS.includes(reason) ? reason : 'manual';
  if (!getToken()) return { status: 'skipped', error: 'brak sesji' };
  if (!shouldSendDiagnostics(r, diagLast)) return { status: 'skipped', error: 'limit 30 min' };
  let p = pack;
  try {
    if (!p) p = await buildDiagnosticsPack({ extra: { ...extra, reason: r, deviceId, label: label?.display || null } });
  } catch (err) {
    p = { queue: [], lost: [], uploadLog: [], stats: {}, meta: { appVersion: APP_VERSION, error: `pack: ${err?.message || err}`, ...extra } };
  }
  const now = Date.now();
  if (isLocalMode() || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    // Kolejka lokalna (max 5, najstarsze wypadają), wysyłka przy wyjściu z trybu / online.
    let q = [];
    try { q = (await getSetting(KEY_DIAG_QUEUE)) || []; } catch { q = []; }
    if (!Array.isArray(q)) q = [];
    q.push({ reason: r, pack: p, at: now });
    while (q.length > DIAG_QUEUE_MAX) q.shift();
    await setSetting(KEY_DIAG_QUEUE, q).catch(() => {});
    diagLast = { ...diagLast, [r]: now };
    await setSetting(KEY_DIAG_LAST, diagLast).catch(() => {});
    return { status: 'queued' };
  }
  try {
    await postDiagnostics(r, p);
    diagLast = { ...diagLast, [r]: now };
    await setSetting(KEY_DIAG_LAST, diagLast).catch(() => {});
    return { status: 'sent' };
  } catch (err) {
    return { status: 'failed', error: err?.message || String(err) };
  }
}

/** Wysyła zaległe pakiety z kolejki lokalnej (po wyjściu z Trybu lokalnego / powrocie sieci). */
export async function flushDiagnosticsQueue() {
  if (!getToken() || isLocalMode()) return 0;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 0;
  let q = [];
  try { q = (await getSetting(KEY_DIAG_QUEUE)) || []; } catch { return 0; }
  if (!Array.isArray(q) || !q.length) return 0;
  let sent = 0;
  const rest = [];
  for (const item of q) {
    try {
      await postDiagnostics(item.reason, { ...item.pack, meta: { ...(item.pack?.meta || {}), queuedAt: new Date(item.at).toISOString() } });
      sent++;
    } catch {
      rest.push(item);
    }
  }
  await setSetting(KEY_DIAG_QUEUE, rest).catch(() => {});
  return sent;
}

/** Wyzwalacz (1): pierwsza niezgodność wersji dla pary (skorupa, latest). */
export async function noteVersionMismatch(shell, latest) {
  if (!latest || compareVersions(latest, shell) <= 0) return false;
  const pair = `${shell}>${latest}`;
  if (mismatchSeen === pair) return false;
  mismatchSeen = pair;
  await setSetting(KEY_MISMATCH_SEEN, pair).catch(() => {});
  const r = await sendDiagnostics('version_mismatch', { extra: { latest, shell } });
  return r.status !== 'skipped';
}

/** Wyzwalacz (2): padnięta instalacja SW (worker `redundant` bez `installed`). */
export async function noteSwInstallFailure(version) {
  const v = version || null;
  if (swInstallFail.version !== v) swInstallFail = { version: v, count: 0, reported: false };
  swInstallFail.count += 1;
  await setSetting(KEY_SW_INSTALL_FAIL, swInstallFail).catch(() => {});
  if (swInstallFail.count >= SW_INSTALL_FAIL_MAX && !swInstallFail.reported) {
    swInstallFail.reported = true;
    await setSetting(KEY_SW_INSTALL_FAIL, swInstallFail).catch(() => {});
    await sendDiagnostics('sw_install_failed', { extra: { installingVersion: v, failures: swInstallFail.count } });
  }
  emit();
  return swInstallFail.count;
}

/** Udana instalacja SW = licznik padnięć od zera. */
export async function noteSwInstallSuccess() {
  if (swInstallFail.count === 0) return;
  swInstallFail = { version: null, count: 0, reported: false };
  await setSetting(KEY_SW_INSTALL_FAIL, swInstallFail).catch(() => {});
  emit();
}

/** Wyzwalacz (3): 3 kolejne nieudane wysyłki (timeout/network/5xx) — zdarzenia uploadManagera. */
export function noteUploadEvent(ev) {
  if (!ev || typeof ev !== 'object') return uploadFailStreak;
  if (ev.type === 'photo-uploaded') { uploadFailStreak = 0; return 0; }
  if (ev.type === 'upload-failed') {
    if (!isUploadStreakError(ev.error)) return uploadFailStreak;
    uploadFailStreak += 1;
    if (uploadFailStreak === UPLOAD_FAIL_STREAK) {
      sendDiagnostics('upload_failures', { extra: { streak: uploadFailStreak, lastError: String(ev.error || '') } }).catch(() => {});
    }
  }
  return uploadFailStreak;
}

export function getUploadFailStreak() { return uploadFailStreak; }

/** Wyzwalacz (4): błąd krytyczny skorupy (window.onerror / unhandledrejection / ErrorBoundary). */
export function reportShellError(source, err, info = {}) {
  const message = err?.message || String(err || 'unknown');
  const stack = String(err?.stack || '').slice(0, 2000);
  return sendDiagnostics('shell_error', { extra: { source, message: message.slice(0, 500), stack, ...info } }).catch(() => ({ status: 'failed' }));
}

let handlersInstalled = false;
/** Globalne haki błędów (raz). */
export function installGlobalErrorHandlers() {
  if (handlersInstalled || typeof window === 'undefined') return false;
  handlersInstalled = true;
  window.addEventListener('error', (ev) => {
    reportShellError('window.onerror', ev?.error || ev?.message, { filename: ev?.filename, lineno: ev?.lineno });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason = ev?.reason;
    // Błędy sieci/API (ApiError: status / offline / timeout, „HTTP 404", „Network error") obsługuje
    // UI — to nie są błędy skorupy; tu tylko prawdziwe wyjątki kodu.
    if (reason?.name === 'AbortError' || reason?.offline || reason?.timeout || reason?.status != null) return;
    if (/^(HTTP \d{3}|Network error|Offline)/.test(String(reason?.message || ''))) return;
    reportShellError('unhandledrejection', reason);
  });
  return true;
}

/** Przełączenie Trybu lokalnego = jeden puls w obie strony (DN1 = a). */
export function installLocalModePulse(sendFn = sendHeartbeat) {
  return subscribeLocalMode((on) => {
    sendFn({ reason: on ? 'local-mode-on' : 'local-mode-off', force: true }).catch(() => {});
  });
}

/** Test hook: reset pamięci (backing IDB resetuje offlineDB.__useMockBacking). */
export function __resetFleetForTest() {
  deviceId = null; label = null; serverInfo = null; lastHeartbeatAt = 0; diagLast = {};
  mismatchSeen = null; swInstallFail = { version: null, count: 0, reported: false };
  uploadFailStreak = 0; loaded = false; listeners.clear(); handlersInstalled = false;
}
